import { useEffect } from 'react';
import { useGetFeaturedQuery } from '@modernhealth/circles-graphql/v2';
import { captureException } from '@sentry/browser';
import { useTranslation } from 'react-i18next';

import { SessionCardSkeleton } from './SessionCardSkeletons';
import { CircleTopicCard } from './CircleTopicCard';
import { CircleGrid, CirclePageSection } from './CirclePageSection';

const Wrapper: React.FC<{ header?: string }> = ({ children, header }) => {
    const { t } = useTranslation('circles');

    return (
        <CirclePageSection header={header ?? t('sections.featured.header')}>
            <CircleGrid>{children}</CircleGrid>
        </CirclePageSection>
    );
};

export const FeaturedTopicsRowSkeleton: React.FC<{ header?: string }> = ({ header }) => {
    return (
        <Wrapper header={header}>
            <SessionCardSkeleton />
            <SessionCardSkeleton />
            <SessionCardSkeleton />
        </Wrapper>
    );
};

export const FeaturedTopicsRow: React.FC<{ header?: string }> = ({ header }) => {
    const { data, loading, error } = useGetFeaturedQuery();

    useEffect(() => {
        if (error) {
            captureException(error);
        }
    }, [error]);

    if (loading && !data) {
        return <FeaturedTopicsRowSkeleton />;
    }

    const { items } = data?.circleFeaturedCollection ?? {};

    const anySessions = items?.some(item => item.sessions.totalCount);
    if (!items || items.length === 0 || !anySessions) return null;

    return (
        <Wrapper header={header}>
            {items.flatMap((topic, i) => {
                if (!topic || topic.sessions.totalCount === 0) return [];

                return (
                    <CircleTopicCard
                        id={`featured-topic-${topic.id}-${i}`}
                        key={`session-card-${topic.id}-${i}`}
                        topic={topic}
                        showSeriesLink
                    />
                );
            })}
        </Wrapper>
    );
};
